import React, { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import DriverHomeMap from './DriverHomeMap';
import TopGreetingBar from './TopGreetingBar';
import OfflineCenterCard from './OfflineCenterCard';
import OnlineWaitingCard, { GoalEditorModal } from './OnlineWaitingCard';
import OpportunitiesSheet from './OpportunitiesSheet';
import TripRequestCard from './TripRequestCard';
import ActiveTripScreen from './ActiveTripScreen';
import TripCompletionCard from './TripCompletionCard';
import DriverBottomNav from './DriverBottomNav';
import { Crosshair } from 'lucide-react';
import VerificationBanner from '../../shared/VerificationBanner';
import ProfilePage from '../../shared/ProfilePage';
import EarningsTab from './EarningsTab';
import HistoryTab from './HistoryTab';
import AccountTab from './AccountTab';
import VoiceCommandOverlay from './VoiceCommandOverlay';
import FatigueAlertModal from './FatigueAlertModal';
import DailyGoalCelebration from './DailyGoalCelebration';
import { refreshUserProfile } from '../../../services/userService';
import { getSocketUrl, SOCKET_PATH } from '../../../utils/apiConfig';
import { pushNotificationService } from '../../../services/pushNotificationService';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Driver Dashboard V2 — map-first, premium driver experience.
 *
 * Phase-1 scope (Home offline + Home online + Trip request).
 * Earnings/History/Account tabs are placeholders that will be replaced in
 * later phases of the redesign.
 */
const DriverDashboardV2 = ({ user, onUpdateUser, onLogout }) => {
  const [activeTab, setActiveTab] = useState('home');
  const [isOnline, setIsOnline] = useState(false);
  const [pendingRideRequest, setPendingRideRequest] = useState(null);
  const [currentRide, setCurrentRide] = useState(null);
  const [tripCompletion, setTripCompletion] = useState(null); // { ride, charges }
  const [showProfile, setShowProfile] = useState(false);

  const [driverLocation, setDriverLocation] = useState(null);
  const [demand, setDemand] = useState({ demand_level: 'medium', surge_active: false, pending_rides_10m: 0 });
  const [metrics, setMetrics] = useState({ trips_today: 0, earned_today: 0, online_seconds: 0, daily_goal: 0, progress_pct: 0 });
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showVoice, setShowVoice] = useState(false);
  const [showFatigue, setShowFatigue] = useState(false);
  const [fatigueDismissedAt, setFatigueDismissedAt] = useState(0);
  const [goalSnoozeUntil, setGoalSnoozeUntil] = useState(0);
  const [goalCelebrated, setGoalCelebrated] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [requestsPausedUntil, setRequestsPausedUntil] = useState(0);
  const [prefs, setPrefs] = useState(null);
  const [_, forceTick] = useState(0); // re-render every second for live timer

  const socketRef = useRef(null);
  const audioRef = useRef(null);

  // Drivers always use light mode
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  const canAcceptRides = (user?.verificationStatus || user?.verification_status) === 'verified';

  // ---- Refresh user + push notifications on mount ----
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const result = await refreshUserProfile(user.id);
      if (!cancelled && result?.success) onUpdateUser(result.user);

      if (canAcceptRides && user.role === 'driver') {
        const ok = await pushNotificationService.initialize(user.id, 'driver');
        if (ok) {
          pushNotificationService.onNotificationReceived((n) => {
            if (n?.data?.type === 'new_ride_request') {
              setPendingRideRequest({
                id: n.data.ride_id,
                pickup_location: { latitude: parseFloat(n.data.pickup_lat), longitude: parseFloat(n.data.pickup_lon), address: n.data.pickup_address },
                estimated_price: parseFloat(n.data.estimated_price),
                vehicle_type: n.data.vehicle_type,
              });
            }
          });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ---- Socket: incoming ride requests ----
  useEffect(() => {
    if (!user?.id) return;
    const sock = io(getSocketUrl(), {
      path: SOCKET_PATH,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });
    socketRef.current = sock;
    sock.on('connect', () => sock.emit('authenticate', { user_id: user.id }));
    sock.on('new_ride_available', (ride) => {
      const isPaused = Date.now() < (requestsPausedUntil || 0);
      if (isOnline && canAcceptRides && !currentRide && !pendingRideRequest && !isPaused) {
        setPendingRideRequest(ride);
        audioRef.current?.play().catch(() => { /* ignore */ });
      }
    });
    return () => sock.disconnect();
  }, [user?.id, isOnline, canAcceptRides, currentRide, pendingRideRequest, requestsPausedUntil]);

  // ---- Geolocation ----
  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (pos) => setDriverLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => { /* ignore — keep previous */ },
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 12000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // ---- POST location to backend every 15s while online so the ride
  //       dispatcher can find this driver via the nearby-drivers filter. ----
  useEffect(() => {
    if (!isOnline || !user?.id || !driverLocation) return undefined;
    const ping = () => {
      fetch(`${API}/api/driver/v2/${user.id}/location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ latitude: driverLocation.lat, longitude: driverLocation.lng }),
      }).catch(() => { /* ignore transient errors */ });
    };
    ping(); // immediate on each location change
    const t = setInterval(ping, 15000);
    return () => clearInterval(t);
  }, [isOnline, user?.id, driverLocation]);

  // ---- Periodic backend pulls (metrics + demand) every 30s ----
  const fetchMetrics = useCallback(async () => {
    if (!user?.id) return;
    try {
      const r = await fetch(`${API}/api/driver/v2/${user.id}/today-metrics`);
      const d = await r.json();
      if (d?.success) setMetrics({
        trips_today: d.trips_today,
        earned_today: d.earned_today,
        online_seconds: d.online_seconds,
        daily_goal: d.daily_goal,
        progress_pct: d.progress_pct,
      });
    } catch { /* ignore */ }
  }, [user?.id]);

  const fetchDemand = useCallback(async () => {
    if (!user?.id) return;
    try {
      const r = await fetch(`${API}/api/driver/v2/${user.id}/demand`);
      const d = await r.json();
      if (d?.success) setDemand(d);
    } catch { /* ignore */ }
  }, [user?.id]);

  useEffect(() => {
    fetchMetrics();
    fetchDemand();
    const t = setInterval(() => { fetchMetrics(); fetchDemand(); }, 30000);
    return () => clearInterval(t);
  }, [fetchMetrics, fetchDemand]);

  // ---- Fetch preferences once (used by fatigue threshold) ----
  useEffect(() => {
    if (!user?.id) return;
    fetch(`${API}/api/driver/v2/${user.id}/preferences`)
      .then((r) => r.json())
      .then((d) => { if (d?.success) setPrefs(d.preferences || null); })
      .catch(() => { /* ignore */ });
  }, [user?.id]);

  // ---- Auto-trigger fatigue alert when online time crosses threshold ----
  useEffect(() => {
    if (!isOnline || !prefs || prefs.fatigue_alert_enabled === false) return;
    const thresholdSecs = (prefs.fatigue_threshold_hours || 4) * 3600;
    const sinceDismiss = Date.now() - (fatigueDismissedAt || 0);
    if ((metrics.online_seconds || 0) >= thresholdSecs && sinceDismiss > 30 * 60 * 1000) {
      setShowFatigue(true);
    }
  }, [metrics.online_seconds, isOnline, prefs, fatigueDismissedAt]);

  // ---- Auto-trigger goal celebration once when daily progress crosses 100% ----
  useEffect(() => {
    if (goalCelebrated || (metrics.daily_goal || 0) <= 0) return;
    if ((metrics.progress_pct || 0) >= 100) {
      setShowCelebration(true);
      setGoalCelebrated(true);
    }
  }, [metrics.progress_pct, metrics.daily_goal, goalCelebrated]);

  // ---- Live online timer (cosmetic; backend recomputes on next poll) ----
  useEffect(() => {
    if (!isOnline) return;
    const t = setInterval(() => {
      setMetrics((m) => ({ ...m, online_seconds: (m.online_seconds || 0) + 1 }));
      forceTick((x) => x + 1);
    }, 1000);
    return () => clearInterval(t);
  }, [isOnline]);

  // ---- Online toggle ----
  const toggleOnline = async (next) => {
    if (!canAcceptRides && next) return;
    setIsOnline(next);
    try {
      await fetch(`${API}/api/driver/v2/${user.id}/online`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_online: next, latitude: driverLocation?.lat ?? null, longitude: driverLocation?.lng ?? null }),
      });
      fetchMetrics();
    } catch { /* keep optimistic UI */ }
  };

  // ---- Demand visualization points (around driver, weighted by demand level) ----
  const demandPoints = (() => {
    if (!driverLocation) return [];
    const base = driverLocation;
    const counts = { low: 4, medium: 8, high: 14 }[demand.demand_level] || 6;
    return Array.from({ length: counts }).map((_, i) => {
      const angle = (i / counts) * 2 * Math.PI;
      const r = 0.008 + ((i % 3) * 0.005); // ~0.8–2km out
      const w = (i % 3) + 1;
      return {
        lat: base.lat + r * Math.cos(angle),
        lng: base.lng + r * Math.sin(angle),
        weight: demand.surge_active && w === 3 ? 3 : w,
      };
    });
  })();

  // ---- Ride request actions ----
  const acceptRide = async (ride) => {
    try {
      const r = await fetch(`${API}/api/rides/${ride.id}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ driver_id: user.id }),
      });
      if (r.ok) {
        const d = await r.json();
        setCurrentRide(d.ride || ride);
      } else {
        const err = await r.text().catch(() => '');
        // Surface a transient warning so the card doesn't just silently vanish.
        // eslint-disable-next-line no-console
        console.warn('Accept ride failed:', r.status, err);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Accept ride network error:', e);
    }
    setPendingRideRequest(null);
  };

  const saveGoal = async (g) => {
    try {
      await fetch(`${API}/api/driver/v2/${user.id}/goal`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ daily_goal: g }),
      });
      setShowGoalModal(false);
      fetchMetrics();
    } catch { /* ignore */ }
  };

  // ---- Voice commands → actions ----
  const handleVoiceCommand = (cmd) => {
    if (!cmd) return;
    switch (cmd.type) {
      case 'accept':
        if (pendingRideRequest) acceptRide(pendingRideRequest);
        break;
      case 'decline':
        if (pendingRideRequest) setPendingRideRequest(null);
        break;
      case 'go_offline':
        if (isOnline) toggleOnline(false);
        break;
      case 'go_online':
        if (!isOnline && canAcceptRides) toggleOnline(true);
        break;
      // 'call', 'message', 'arrived', 'start_trip', 'end_trip', 'report' are scoped
      // to the active trip screen; that screen owns its own voice overlay.
      default:
        break;
    }
  };

  const handleTakeBreak = ({ minutes }) => {
    setShowFatigue(false);
    setFatigueDismissedAt(Date.now());
    setRequestsPausedUntil(Date.now() + minutes * 60 * 1000);
  };

  // If a ride is in progress, hand off to the redesigned ActiveTripScreen
  if (currentRide) {
    return (
      <>
        <ActiveTripScreen
          ride={currentRide}
          driverId={user?.id}
          onComplete={(payload) => {
            setCurrentRide(null);
            setTripCompletion(payload);
            fetchMetrics();
          }}
        />
        {tripCompletion && (
          <TripCompletionCard
            completion={tripCompletion}
            driverId={user?.id}
            onBackOnline={() => {
              setTripCompletion(null);
              if (!isOnline) toggleOnline(true);
            }}
            onBreak={() => {
              setTripCompletion(null);
              toggleOnline(false);
            }}
          />
        )}
      </>
    );
  }

  // If we're back at the dashboard but a completion card is queued (e.g. ride
  // ended via push notification), show it as an overlay on top of the dashboard.
  const completionOverlay = tripCompletion ? (
    <TripCompletionCard
      completion={tripCompletion}
      driverId={user?.id}
      onBackOnline={() => {
        setTripCompletion(null);
        if (!isOnline) toggleOnline(true);
      }}
      onBreak={() => {
        setTripCompletion(null);
        toggleOnline(false);
      }}
    />
  ) : null;

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-gray-100" data-testid="driver-dashboard-v2">
      {/* Map background — only on Home tab */}
      {activeTab === 'home' && (
        <DriverHomeMap driverLocation={driverLocation} demandPoints={demandPoints} />
      )}

      {/* Top bar — always visible except when a tab uses its own scroller (earnings/history) */}
      <TopGreetingBar
        user={user}
        isOnline={isOnline}
        onlineForSeconds={metrics.online_seconds}
        unreadCount={0}
        onBellClick={() => { /* phase 4: surface notifications */ }}
      />

      {/* Verification banner */}
      {!canAcceptRides && (
        <div className="absolute top-20 left-3 right-3 z-[35]">
          <VerificationBanner user={user} onStartVerification={() => setShowProfile(true)} />
        </div>
      )}

      {/* HOME TAB */}
      {activeTab === 'home' && (
        <>
          {/* Center card / waiting card */}
          {!isOnline ? (
            <div className="absolute left-3 right-3 z-[42]" style={{ top: 'min(40vh, 280px)' }}>
              <OfflineCenterCard
                estimatedNext4h={metrics.daily_goal && metrics.progress_pct < 50
                  ? Math.round((metrics.daily_goal - metrics.earned_today) * 0.55)
                  : Math.round((metrics.earned_today / Math.max(1, metrics.online_seconds / 3600)) * 4) || 12000}
                demandLevel={demand.demand_level}
                surgeActive={demand.surge_active}
                onGoOnline={() => toggleOnline(true)}
                disabled={!canAcceptRides}
                disabledReason={!canAcceptRides ? 'Complete verification to start accepting rides' : ''}
              />
            </div>
          ) : (
            <div
              className="absolute left-3 right-3 z-[42]"
              style={{ bottom: 'calc(180px + env(safe-area-inset-bottom, 0))' }}
            >
              <OnlineWaitingCard
                metrics={metrics}
                onEditGoal={() => setShowGoalModal(true)}
                onGoOffline={() => toggleOnline(false)}
              />
            </div>
          )}

          {/* Recenter / destination filter button */}
          <button
            type="button"
            data-testid="driver-recenter-btn"
            onClick={() => {
              if (!navigator.geolocation) return;
              navigator.geolocation.getCurrentPosition((p) => setDriverLocation({ lat: p.coords.latitude, lng: p.coords.longitude }));
            }}
            className="fixed right-4 z-[44] w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 active:scale-95 transition-all"
            style={{ bottom: 'calc(170px + env(safe-area-inset-bottom, 0))' }}
            aria-label="Recenter map"
          >
            <Crosshair className="w-5 h-5 text-emerald-600" />
          </button>

          {/* Voice command floating button */}
          <button
            type="button"
            data-testid="driver-voice-btn"
            onClick={() => setShowVoice(true)}
            className="fixed left-4 z-[44] w-12 h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg flex items-center justify-center active:scale-95 transition-all"
            style={{ bottom: 'calc(170px + env(safe-area-inset-bottom, 0))' }}
            aria-label="Voice commands"
          >
            <span className="font-bold text-xl">🎤</span>
          </button>

          {/* Paused-requests pill */}
          {Date.now() < requestsPausedUntil && isOnline && (
            <div
              className="fixed z-[44] left-3 right-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl py-2 px-3 text-center text-[12px] font-semibold shadow"
              style={{ bottom: 'calc(232px + env(safe-area-inset-bottom, 0))' }}
              data-testid="driver-paused-pill"
            >
              Requests paused — back in {Math.max(1, Math.ceil((requestsPausedUntil - Date.now()) / 60000))} min
            </div>
          )}

          {/* Opportunities sheet */}
          <OpportunitiesSheet driverId={user?.id} />
        </>
      )}

      {/* EARNINGS TAB */}
      {activeTab === 'earnings' && (
        <EarningsTab driverId={user?.id} />
      )}

      {/* HISTORY TAB */}
      {activeTab === 'history' && (
        <HistoryTab driverId={user?.id} />
      )}

      {/* ACCOUNT TAB */}
      {activeTab === 'account' && (
        <AccountTab
          user={user}
          onOpenProfile={() => setShowProfile(true)}
          onLogout={onLogout}
        />
      )}

      {/* Bottom nav — always visible */}
      <DriverBottomNav activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Trip request overlay */}
      {pendingRideRequest && (
        <TripRequestCard
          ride={pendingRideRequest}
          countdown={20}
          onAccept={acceptRide}
          onDecline={() => setPendingRideRequest(null)}
          onTimeout={() => setPendingRideRequest(null)}
        />
      )}

      {/* Goal modal */}
      <GoalEditorModal
        open={showGoalModal}
        currentGoal={metrics.daily_goal}
        onSave={saveGoal}
        onClose={() => setShowGoalModal(false)}
      />

      {/* Profile modal */}
      {showProfile && (
        <ProfilePage user={user} onUpdateUser={onUpdateUser} onClose={() => setShowProfile(false)} rideHistory={[]} />
      )}

      {/* Phase 4 overlays */}
      {showVoice && (
        <VoiceCommandOverlay
          onCommand={handleVoiceCommand}
          onClose={() => setShowVoice(false)}
        />
      )}
      {showFatigue && (
        <FatigueAlertModal
          onlineSeconds={metrics.online_seconds}
          thresholdHours={prefs?.fatigue_threshold_hours || 4}
          onTakeBreak={handleTakeBreak}
          onContinue={() => { setShowFatigue(false); setFatigueDismissedAt(Date.now()); }}
        />
      )}
      {showCelebration && (
        <DailyGoalCelebration
          earned={metrics.earned_today}
          goal={metrics.daily_goal}
          onGoOffline={() => { setShowCelebration(false); toggleOnline(false); }}
          onKeepEarning={() => setShowCelebration(false)}
        />
      )}

      {/* Notification sound */}
      <audio ref={audioRef} src="/notification.mp3" preload="auto" />

      {completionOverlay}
    </div>
  );
};

export default DriverDashboardV2;
